import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LanguageDTO } from "../../dto/LanguageDTO";
import LocalizedLanguageDTO from "../../dto/LocalizedLanguageDTO";
import { RootState } from "../Store";

export interface LocalizedString {
  [key: string]: { value: string; isAutoTranslated: boolean };
}

export interface LocalizedStringRecord {
  [key: string]: LocalizedString;
}

export interface LanguageData {
  shortcode: string;
  full: string;
  includeInPublish?: boolean;
}

export interface TrainingLocalizationState {
  localizedEntries: LocalizedStringRecord;
  propLocalizedEntries: LocalizedStringRecord;
  localizedLanguages: LanguageData[];
  availableLanguages: LanguageData[];
  currentLanguage: LanguageData;
  initialized: boolean;
}

const mockupLanguages: LanguageData[] = [
  { shortcode: "nl", full: "Nederlands" },
];

const mockupAvailableLanguages: LanguageData[] = [
  { shortcode: "nl", full: "Nederlands" },
  { shortcode: "en", full: "English" },
  { shortcode: "fr", full: "Francais" },
];

const initialState: TrainingLocalizationState = {
  localizedEntries: {},
  propLocalizedEntries: {},
  localizedLanguages: mockupLanguages,
  currentLanguage: mockupLanguages[0],
  availableLanguages: mockupAvailableLanguages,
  initialized: false,
};

export const trainingLocalizationSlice = createSlice({
  name: "trainingLocalization",
  initialState: initialState,
  reducers: {
    hydrateLocalization: (
      _state,
      action: PayloadAction<TrainingLocalizationState>
    ) => {
      return {
        ...action.payload,
        propLocalizedEntries: action.payload.propLocalizedEntries ?? {},
      };
    },
    clearLocalization: (state) => {
      state.localizedEntries = {};
      state.propLocalizedEntries = {};
      state.localizedLanguages = [];
      state.initialized = false;
    },
    initializeNewLocalization: (state) => {
      state.localizedLanguages = [mockupLanguages[0]];
    },
    registerNewKey: (state, action: PayloadAction<string>) => {
      state.localizedEntries[action.payload] = {};

      state.localizedLanguages.forEach((lang) => {
        state.localizedEntries[action.payload][lang.shortcode] = {
          isAutoTranslated: false,
          value: "",
        };
      });
    },
    registerNewKeys: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((key) => {
        state.localizedEntries[key] = {};

        state.localizedLanguages.forEach((lang) => {
          state.localizedEntries[key][lang.shortcode] = {
            isAutoTranslated: false,
            value: "",
          };
        });
      });
    },
    registerPropLocalizedKeys: (state, action: PayloadAction<string[]>) => {
      const keys = action.payload;
      for (const key of keys) {
        if (key in state.propLocalizedEntries) continue;

        const existingEntry = state.localizedEntries[key];
        if (existingEntry) {
          state.propLocalizedEntries[key] = existingEntry;
          delete state.localizedEntries[key];
          continue;
        }

        state.propLocalizedEntries[key] = {};
        for (const lang of state.localizedLanguages) {
          state.propLocalizedEntries[key][lang.shortcode] = {
            value: "",
            isAutoTranslated: false,
          };
        }
      }
    },
    setCurrentLanguage: (state, action: PayloadAction<LanguageData>) => {
      state.currentLanguage = action.payload;
    },
    addLocalizedLanguage: (state, action: PayloadAction<LanguageData>) => {
      for (const key in state.localizedEntries) {
        state.localizedEntries[key][action.payload.shortcode] = {
          isAutoTranslated: false,
          value: "",
        };
      }

      for (const key in state.propLocalizedEntries) {
        state.propLocalizedEntries[key][action.payload.shortcode] = {
          isAutoTranslated: false,
          value: "",
        };
      }

      state.localizedLanguages.push(action.payload);
    },
    addAutoTranslatedLanguage: (
      state,
      action: PayloadAction<{ lang: LanguageData; translatedValues: string[] }>
    ) => {
      let index = 0;
      // A value that is not a string means the translation did not arrive for
      // this key. Write an empty, human-editable entry rather than `undefined`
      // marked as auto-translated: the language still gets its entries, and the
      // gaps read as missing instead of looking like finished translations.
      const entryFor = (position: number) => {
        const value = action.payload.translatedValues[position];
        return typeof value === "string"
          ? { isAutoTranslated: true, value }
          : { isAutoTranslated: false, value: "" };
      };

      for (const key in state.localizedEntries) {
        state.localizedEntries[key][action.payload.lang.shortcode] =
          entryFor(index);
        index += 1;
      }

      for (const key in state.propLocalizedEntries) {
        state.propLocalizedEntries[key][action.payload.lang.shortcode] =
          entryFor(index);
        index += 1;
      }

      if (
        !state.localizedLanguages.find(
          (lang) => lang.shortcode === action.payload.lang.shortcode
        )
      ) {
        state.localizedLanguages.push(action.payload.lang);
      }
    },
    addMissingAutoTranslatedKeys: (
      state,
      action: PayloadAction<{ lang: LanguageData; translatedValues: string[] }>
    ) => {
      const lang = action.payload.lang;
      const values = action.payload.translatedValues;
      var index = 0;

      for (const key in state.localizedEntries) {
        if (
          (!state.localizedEntries[key][lang.shortcode].value ||
            state.localizedEntries[key][lang.shortcode].value === " ") &&
          // Without this, a short result array marks an entry auto-translated
          // while leaving it blank — it then no longer counts as missing.
          typeof values[index] === "string"
        ) {
          state.localizedEntries[key][lang.shortcode] = {
            value: values[index],
            isAutoTranslated: true,
          };
        }

        index += 1;
      }

      for (const key in state.propLocalizedEntries) {
        const currentValue =
          state.propLocalizedEntries[key][lang.shortcode]?.value;
        if (
          (!currentValue || !currentValue.trim()) &&
          typeof values[index] === "string"
        ) {
          state.propLocalizedEntries[key][lang.shortcode] = {
            value: values[index],
            isAutoTranslated: true,
          };
        }

        index += 1;
      }
    },
    deleteLocalizedLanguage: (state, action: PayloadAction<LanguageData>) => {
      const index = state.localizedLanguages.indexOf(
        state.localizedLanguages.find(
          (lang) => lang.shortcode === action.payload.shortcode
        ) as LanguageData
      );
      state.localizedLanguages.splice(index, 1);

      if (state.currentLanguage.shortcode === action.payload.shortcode) {
        state.currentLanguage = state.localizedLanguages[0];
      }
    },
    updateLocalizedString: (
      state,
      action: PayloadAction<{
        key: string;
        newValue: string;
        shortcode?: string;
      }>
    ) => {
      const { key, newValue, shortcode } = action.payload;
      const targetLanguage = shortcode ?? state.currentLanguage.shortcode;

      if (!state.localizedEntries[key]) {
        state.localizedEntries[key] = {};
      }

      if (!state.localizedEntries[key][targetLanguage]) {
        state.localizedEntries[key][targetLanguage] = {
          value: "",
          isAutoTranslated: false,
        };
      }

      state.localizedEntries[key][targetLanguage] = {
        value: newValue,
        isAutoTranslated: false,
      };
    },
    updatePropLocalizedString: (
      state,
      action: PayloadAction<{
        key: string;
        shortcode: string;
        newValue: string;
      }>
    ) => {
      const { key, shortcode, newValue } = action.payload;
      state.propLocalizedEntries[key] ??= {};
      state.propLocalizedEntries[key][shortcode] = {
        value: newValue,
        isAutoTranslated: false,
      };

      // A metadata-declared Prop key has one owner. Removing a legacy regular
      // copy keeps Excel and serialized payloads free of duplicate keys.
      delete state.localizedEntries[key];
    },
    applyPropLocalizedTranslations: (
      state,
      action: PayloadAction<{
        translations: { key: string; shortcode: string; value: string }[];
      }>
    ) => {
      for (const { key, shortcode, value } of action.payload.translations) {
        state.propLocalizedEntries[key] ??= {};
        state.propLocalizedEntries[key][shortcode] = {
          value,
          isAutoTranslated: true,
        };
        delete state.localizedEntries[key];
      }
    },
    updateLocalizedStringFromExcel: (
      state,
      action: PayloadAction<{ key: string; shortcode: string; value: string }>
    ) => {
      const { key, shortcode, value } = { ...action.payload };
      if (
        state.localizedLanguages.find(
          (lang) => lang.shortcode === shortcode
        ) === undefined
      )
        return;

      const propEntry = state.propLocalizedEntries[key];
      const targetEntry = propEntry ?? state.localizedEntries[key];
      if (targetEntry === undefined) return;

      targetEntry[shortcode] = {
        value: value,
        isAutoTranslated: false,
      };

      if (propEntry) delete state.localizedEntries[key];
    },
    updateLocalizedRecord: (
      state,
      action: PayloadAction<{
        key: string;
        entries: { shortcode: string; value: string }[];
      }>
    ) => {
      const { key, entries } = { ...action.payload };

      entries.forEach((entry) => {
        state.localizedEntries[key][entry.shortcode] = {
          value: entry.value,
          isAutoTranslated:
            state.localizedEntries[key][entry.shortcode].isAutoTranslated,
        };
      });
    },
    mergeLocalizedEntryValues: (
      state,
      action: PayloadAction<{ key: string; entries: LocalizedString }>
    ) => {
      const { key, entries } = action.payload;
      state.localizedEntries[key] ??= {};

      Object.entries(entries).forEach(([shortcode, localizedValue]) => {
        state.localizedEntries[key][shortcode] = { ...localizedValue };
      });
    },
    addLocalizedKeys: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((key) => {
        state.localizedEntries[key] = {};
      });
    },
    addSerializedLanguage: (
      state,
      action: PayloadAction<LocalizedLanguageDTO>
    ) => {
      const dto = action.payload;
      const language = state.availableLanguages.find(
        (lang) => lang.shortcode === dto.lang
      );

      if (
        !state.localizedLanguages.find(
          (lang) => lang.shortcode === language?.shortcode
        )
      ) {
        state.localizedLanguages.push({
          ...(language as LanguageData),
          includeInPublish: dto.includeInPublish ?? true,
        });
      }

      dto.entries.forEach((entry) => {
        state.localizedEntries[entry.key][dto.lang] = {
          value: entry.value,
          isAutoTranslated: entry.isAutoTranslated,
        };
      });
    },
    addSupportedLanguages: (state, action: PayloadAction<LanguageDTO[]>) => {
      var langData: LanguageData[] = [];
      action.payload.map((lang) => {
        langData.push({
          shortcode: lang.id,
          full: lang.name,
        });
      });
      state.availableLanguages = langData;
    },
    setInitialLanguage: (state, action: PayloadAction<string>) => {
      var initLang = state.localizedLanguages.find(
        (lang) => lang.shortcode === action.payload
      );
      if (initLang) {
        state.currentLanguage = initLang;
        var index = state.localizedLanguages.indexOf(initLang);
        const locLang = [...state.localizedLanguages];
        const temp = locLang[index];
        locLang[index] = locLang[0];
        locLang[0] = temp;
        state.localizedLanguages = locLang;
      }

      state.initialized = true;
    },
    deleteKeys: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((key) => {
        delete state.localizedEntries[key];
      });
    },
    UpdateSetOfKeys: (
      state,
      action: PayloadAction<{ lang: string; keys: string[]; values: string[] }>
    ) => {
      const { lang, keys, values } = { ...action.payload };
      keys.forEach((key, index) => {
        const value = values[index];
        // Values pair with keys by index, so a short array would blank the tail.
        // Leave what is already there rather than replacing it with `undefined`.
        if (typeof value !== "string") return;

        state.localizedEntries[key][lang] = {
          value,
          isAutoTranslated: true,
        };
      });
    },
    setLanguageIncludeInPublish: (
      state,
      action: PayloadAction<{ shortcode: string; includeInPublish: boolean }>
    ) => {
      const language = state.localizedLanguages.find(
        (lang) => lang.shortcode === action.payload.shortcode
      );
      if (language) {
        language.includeInPublish = action.payload.includeInPublish;
      }
    },
  },
});

export const selectTrainingLocalizationState = (state: RootState) => {
  return state.trainingLocalization;
};

export const selectLocalizedLanguages = (state: RootState) =>
  state.trainingLocalization.localizedLanguages;

export const selectCurrentLanguage = (state: RootState) =>
  state.trainingLocalization.currentLanguage;

export const selectAvailableLanguagesNotAdded = createSelector(
  [
    (state: RootState) => state.trainingLocalization.availableLanguages,
    (state: RootState) => state.trainingLocalization.localizedLanguages,
  ],
  (availableLanguages, localizedLanguages) => {
    return availableLanguages.filter(
      (lang) =>
        !localizedLanguages.some(
          (locLang) => locLang.shortcode === lang.shortcode
        )
    );
  }
);

export const selectAvailableLanguages = (state: RootState) =>
  state.trainingLocalization.availableLanguages;

export const {
  hydrateLocalization,
  clearLocalization,
  registerNewKey,
  registerNewKeys,
  registerPropLocalizedKeys,
  setCurrentLanguage,
  updateLocalizedString,
  updatePropLocalizedString,
  applyPropLocalizedTranslations,
  updateLocalizedStringFromExcel,
  updateLocalizedRecord,
  mergeLocalizedEntryValues,
  addLocalizedLanguage,
  addAutoTranslatedLanguage,
  addMissingAutoTranslatedKeys,
  deleteLocalizedLanguage,
  addLocalizedKeys,
  addSerializedLanguage,
  addSupportedLanguages,
  setInitialLanguage,
  initializeNewLocalization,
  deleteKeys,
  UpdateSetOfKeys,
  setLanguageIncludeInPublish,
} = trainingLocalizationSlice.actions;

export default trainingLocalizationSlice.reducer;

export const selectLocalizedEntries = (state: RootState) =>
  state.trainingLocalization.localizedEntries;

export const selectPropLocalizedEntries = (state: RootState) =>
  state.trainingLocalization.propLocalizedEntries;

export const requestNewKey = (state: TrainingLocalizationState) => {
  let newKey = "";
  let currIndex = 1;
  do {
    newKey = formatIndexToKey(currIndex);
    currIndex += 1;
  } while (newKey in state.localizedEntries);

  return newKey;
};

export const requestArrayOfKeys = (
  state: TrainingLocalizationState,
  count: number
) => {
  const keys: string[] = [];

  let newKey = "";
  let currIndex = 1;
  for (let i = 0; i < count; i++) {
    do {
      newKey = formatIndexToKey(currIndex);
      currIndex += 1;
    } while (newKey in state.localizedEntries || keys.indexOf(newKey) > -1);
    keys.push(newKey);
  }

  return keys;
};

export const selectIsLocalizationInitialized = (state: RootState) =>
  state.trainingLocalization.initialized;

export const selectLanguageIncludeInPublish = (shortcode: string) =>
  createSelector(
    [(state: RootState) => state.trainingLocalization.localizedLanguages],
    (localizedLanguages) => {
      const language = localizedLanguages.find((lang) => lang.shortcode === shortcode);
      return language?.includeInPublish ?? true;
    }
  );

export const selectAllLanguagesWithPublishStatus = createSelector(
  [(state: RootState) => state.trainingLocalization.localizedLanguages],
  (localizedLanguages) =>
    localizedLanguages.map((lang) => ({
      shortcode: lang.shortcode,
      full: lang.full,
      includeInPublish: lang.includeInPublish ?? true,
    }))
);

const formatIndexToKey = (index: number) => {
  if (index < 10) return `key_00${index}`;
  else if (index >= 10 && index < 100) return `key_0${index}`;
  else if (index >= 100 && index < 1000) return `key_${index}`;
  else {
    throw new Error("too many keys in localization!");
  }
};
